import { Component, ElementRef, OnInit, SecurityContext, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalClickEvent } from '@shared/models/modal-click-event';
import { GoogleAuthStatus } from '@shared/services/integrations/google/auth/google-auth-status-interface';
import { GoogleAuthService } from '@shared/services/integrations/google/auth/google-auth.service';
import MarkdownIt from 'markdown-it';
import { catchError, finalize, first, map, of, switchMap, take } from 'rxjs';
import { NotificationModel, NotificationsService, NotificationTypes } from '../../../shared/services/notifications/notifications.service';
import { SupplierCustomerStatus, SupplierCustomerStatusLabels } from '../enums/supplier-customer-status.enum';
import { Customer } from '../interfaces/customer.interface';
import { CustomerService } from '../services/customer.service';
import { SupplierCustomerAcquisition } from '../enums/supplier-customer-acquisition.enum';
import { NavigationStateService } from '@shared/services';

@Component({
    selector: 'customer-profile-component',
    templateUrl: './customer-profile.control.html',
    styleUrls: ['./customer-profile.control.scss'],
    standalone: false
})
export class CustomerProfileComponent implements OnInit {
  @ViewChild('deleteModal') deleteModal: ElementRef;
  customer: Customer;
  isLoading = false;
  isToggleStatusLoading = false;
  isDeleteLoading = false;
  customerUuid = '';
  statusConfig: { name?: string; classes?: string };
  isSetArchiveLoading = false;
  isGmailConnected = false;
  private converter: MarkdownIt;

  constructor(
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _customerService: CustomerService,
    private _notificationsService: NotificationsService,
    private _navService: NavigationStateService,
    private sanitizer: DomSanitizer,
    private _googleAuthService: GoogleAuthService
  ) {}

  get htmlContent(): SafeHtml {
    const normalized = (this.customer?.notes || '').replace(/&nbsp;|\u00A0/g, ' ');
    const html = this.converter.render(normalized);
    return this.sanitizer.sanitize(SecurityContext.HTML, html);
  }

  ngOnInit(): void {
    this.customerUuid = this._activatedRoute.snapshot?.params.customerUuid;
    if (this.customerUuid) {
      this.getCustomer(this.customerUuid).subscribe();
    } else {
      console.error('No customer UUID provided in route parameters');
    }

    this.converter = new MarkdownIt({
      html: true,
      breaks: true,
      linkify: true
    });

    this._googleAuthService
      .getStatus$({ scope: 'email' })
      .pipe(first())
      .subscribe({
        next: status => {
          this.isGmailConnected = !!(status as GoogleAuthStatus)?.connected;
        }
      });
  }

  editCustomer() {
    this._router.navigate(['edit', this.customer.uuid], {
      relativeTo: this._activatedRoute.parent
    });
  }

  deleteCustomer(event: CustomEvent<{ modalClickEvent: ModalClickEvent }>) {
    if (event.detail?.modalClickEvent !== ModalClickEvent.CONFIRM_BUTTON_CLICKED) return;

    if (!this.customer?.uuid) {
      console.error('No customer UUID provided when trying to delete customer');
      return;
    }
    this.isDeleteLoading = true;

    this._customerService
      .deleteCustomer(this.customer.uuid)
      .pipe(
        first(),
        finalize(() => (this.isDeleteLoading = false))
      )
      .subscribe({
        next: () => {
          const notification: NotificationModel = {
            message: `Customer Deleted.`,
            action: 'Close',
            type: NotificationTypes.SUCCESS
          };
          this._notificationsService.openSnackBar(notification);
        },
        error: error => {
          console.error(error);
          const notification: NotificationModel = {
            message: error.message || `Error deleting customer with ID ${this.customer?.uuid}. Please try again.`,
            action: 'Close',
            type: NotificationTypes.ERROR
          };
          this._notificationsService.openSnackBar(notification);
        }
      });
  }

  getStatusConfig(status: SupplierCustomerStatus) {
    const name = SupplierCustomerStatusLabels[status];
    if (!name) {
      return { name: 'Status unknown', classes: 'tw-text-slate-600 tw-bg-slate-200' };
    }
    const classes = status === SupplierCustomerStatus.COMPLETED
      ? 'tw-text-slate-800 tw-bg-slate-200'
      : 'tw-text-green-800 tw-bg-green-200';
    return { name, classes };
  }

  setArchiveStatus(event: CustomEvent<{ modalClickEvent: ModalClickEvent }>, isArchived = false) {
    event.stopPropagation();

    if (event.detail?.modalClickEvent === ModalClickEvent.CANCEL_BUTTON_CLICKED) {
      return;
    }

    if (!this.customer?.uuid) {
      console.error(`No customer UUID provided when trying to ${isArchived ? 'unarchive' : 'archive'} customer`);
      return;
    }
    this.isSetArchiveLoading = true;

    this._customerService
      .updateCustomer(this.customer.uuid, { isArchived })
      .pipe(
        first(),
        switchMap(() => this.getCustomer(this.customerUuid)),
        finalize(() => (this.isSetArchiveLoading = false))
      )
      .subscribe({
        next: () => {
          const notification: NotificationModel = {
            message: `Customer ${isArchived ? 'archived' : 'unarchived'}.`,
            action: 'Close',
            type: NotificationTypes.SUCCESS
          };
          this._notificationsService.openSnackBar(notification);
        },
        error: error => {
          console.error(error);
          const notification: NotificationModel = {
            message: error.message || `Error ${isArchived ? 'unarchiving' : 'archiving'} customer with ID ${this.customer.uuid}. Please try again.`,
            action: 'Close',
            type: NotificationTypes.ERROR
          };
          this._notificationsService.openSnackBar(notification);
        }
      });
  }

  /**
   * Get a customer by its UUID and patch the form with the returned values.
   *
   * @param customerUuid The UUID of the customer to get.
   */
  getCustomer(customerUuid: string) {
    if (!customerUuid) {
      const errorMessage = 'No customer UUID provided when trying to get customer';
      console.error(errorMessage);
      return of(new Error(errorMessage));
    }

    this.isLoading = true;

    return this._customerService.getCustomer(customerUuid).pipe(
      take(1),
      map(response => {
        if (!response) {
          return;
        }
        this.customer = response;
        this.statusConfig = this.getStatusConfig(this.customer?.status);
      }),
      catchError(error => {
        console.error(error);
        const notification: NotificationModel = {
          message: error.message || `Error getting Customer with ID ${customerUuid}. Please try again.`,
          action: 'Close',
          type: NotificationTypes.ERROR
        };
        this._notificationsService.openSnackBar(notification);
        return of(error);
      }),
      finalize(() => (this.isLoading = false))
    );
  }

  openConfirmationModal() {
    if (this.deleteModal?.nativeElement && typeof this.deleteModal.nativeElement.open === 'function') {
      this.deleteModal.nativeElement.open();
    }
  }

  navigateBack() {
    this._navService.goBack();
  }

  get formattedAddress(): string {
    const addr = this.customer?.address;
    if (!addr) return '';
    return [addr.line1, addr.line2, addr.city, addr.county, addr.postcode].filter(Boolean).join('\n');
  }

  composeEmail() {
    this._router.navigate(['../emails/compose'], {
      relativeTo: this._activatedRoute.parent,
      queryParams: { to: this.customer.email }
    });
  }

  get acquisitionLabel(): string {
    if (!this.customer?.acquisition) return '';
    return this.customer.acquisition === SupplierCustomerAcquisition.Request
      ? 'Add To Event Request'
      : this.customer.acquisition.charAt(0).toUpperCase() + this.customer.acquisition.slice(1);
  }
}
